import api from "../services/index";

// Param names and their sentinels live here so renaming one is a single edit.
// ALL and "" are OMITTED, never sent: an unknown or empty value is ignored
// server-side, which would leave the feed looking filtered but unfiltered.
const feedParams = ({
  page = 0,
  size = 10,
  sort = "NEWEST",
  activity = "ALL",
  audienceId = "",
} = {}) => ({
  page,
  size,
  sort: sort || undefined,
  // ALL and "" are UI sentinels, never wire values. They must be `undefined`,
  // not null or "" - fetchBaseQuery's stripUndefined only drops undefined, so
  // any other empty value would be serialized and silently ignored by the
  // server, leaving the feed looking filtered but unfiltered.
  activity: activity && activity !== "ALL" ? activity : undefined,
  audienceId: audienceId || undefined,
});

const engageApi = api
  .enhanceEndpoints({ addTagTypes: ["EngageFeed"] })
  .injectEndpoints({
    endpoints: (builder) => ({
      // Args { page, size, sort, activity, audienceId } are the cache key, so
      // each filter combination caches separately and the screen must pass the
      // identical object to updateQueryData when patching a post.
      getEngageFeed: builder.query({
        query: (args) => ({
          url: "/api/engage/feed",
          method: "GET",
          params: feedParams(args),
        }),
        transformResponse: (response) => ({
          items: Array.isArray(response?.data) ? response.data : [],
          totalCount: response?.totalcount == null ? 0 : response.totalcount,
          pageSize: response?.pagesize == null ? 0 : response.pagesize,
          currentPage: response?.currentpage == null ? 0 : response.currentpage,
          totalPages: response?.totalpages == null ? 0 : response.totalpages,
        }),
        providesTags: ["EngageFeed"],
      }),

      getEngageRecipients: builder.query({
        query: () => ({
          url: "/api/engage/recipients",
          method: "GET",
        }),
        transformResponse: (response) => response?.data || [],
      }),

      getEngageAudiences: builder.query({
        query: () => ({
          url: "/api/engage/audiences",
          method: "GET",
        }),
        transformResponse: (response) => response?.data || {},
      }),

      getRecognizeValues: builder.query({
        query: () => ({
          url: "/api/engage/recognize/values",
          method: "GET",
        }),
        transformResponse: (response) => response?.data || [],
      }),

      createKudos: builder.mutation({
        query: (body) => ({
          url: "/api/engage/kudos",
          method: "POST",
          body,
        }),
        transformResponse: (response) => response?.data || null,
        invalidatesTags: ["EngageFeed"],
      }),

      // { title, body, audience: { audienceType, audienceId }, image? }
      // image is the s3Document DTO only:
      // { fileName, folderPath, timeStamp, bucketName, region }
      createAnnouncement: builder.mutation({
        query: (body) => ({
          url: "/api/engage/announcement",
          method: "POST",
          body,
        }),
        transformResponse: (response) => response?.data || null,
        invalidatesTags: ["EngageFeed"],
      }),

      sendEngageMail: builder.mutation({
        query: (body) => ({
          url: "/api/public/chat",
          method: "POST",
          body,
        }),
        transformResponse: (response) => response?.data ?? null,
      }),

      createPoll: builder.mutation({
        query: (body) => ({
          url: "/api/engage/poll",
          method: "POST",
          body,
        }),
        transformResponse: (response) => response?.data || null,
        invalidatesTags: ["EngageFeed"],
      }),

      votePoll: builder.mutation({
        query: ({ postId, optionId }) => ({
          url: `/api/engage/poll/${postId}/vote`,
          method: "POST",
          body: { optionId },
        }),
        transformResponse: (response) => response?.data || null,
      }),

      setReactions: builder.mutation({
        query: ({ segment, postId, emoji }) => ({
          url: `/api/engage/${segment}/${postId}/react`,
          method: "POST",
          body: { emoji },
        }),
        transformResponse: (response) =>
          Array.isArray(response?.data) ? response.data : [],
      }),

      getPostReactions: builder.query({
        query: ({ segment, postId }) => ({
          url: `/api/engage/${segment}/${postId}/reactions`,
          method: "GET",
        }),
        transformResponse: (response) =>
          Array.isArray(response?.data) ? response.data : [],
        keepUnusedDataFor: 0,
      }),
    }),
  });

export default engageApi;

export const {
  useGetEngageFeedQuery,
  useGetEngageRecipientsQuery,
  useGetEngageAudiencesQuery,
  useGetRecognizeValuesQuery,
  useCreateKudosMutation,
  useCreateAnnouncementMutation,
  useSendEngageMailMutation,
  useCreatePollMutation,
  useVotePollMutation,
  useSetReactionsMutation,
  useGetPostReactionsQuery,
  useLazyGetPostReactionsQuery,
} = engageApi;
